import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useGameStore } from '../store/gameStore'
import type { MistakeEntry } from '../types'

type FilterMode = 'all' | 'frequent' | 'recent'

function MistakeBookPage() {
  const mistakeEntries = useGameStore(s => s.mistakeEntries)
  const removeMistake = useGameStore(s => s.removeMistake)
  const [filter, setFilter] = useState<FilterMode>('all')

  if (mistakeEntries.length === 0) {
    return (
      <div className="min-h-screen p-4 md:p-6 crt-scanlines">
        <div className="max-w-2xl mx-auto">
          <Link
            to="/"
            className="font-pixel-display text-[10px] text-[color:var(--color-cyan)] hover:text-[color:var(--color-bull)] mb-3 inline-block"
          >
            ◀ 返回首页
          </Link>
          <div className="pixel-box pixel-box-bright p-4 mb-4 text-center">
            <div className="font-pixel text-[10px] text-[color:var(--color-gold)] mb-2 tracking-wider">
              ► 错题本
            </div>
            <h1 className="font-pixel text-xl text-[color:var(--color-text-bright)] mb-1">
              暂时没有错题
            </h1>
            <p className="font-pixel-body text-sm text-[color:var(--color-text-dim)] mt-1">
              ► 答错会自动收入错题本, 复盘可加快掌握
            </p>
          </div>
        </div>
      </div>
    )
  }

  // 把 cardId 解析成可读路径与路由
  const parseCardPath = (cardId: string) => {
    const m = cardId.match(/^ch(\d+)-s(\d+)-c(\d+)$/)
    if (m) {
      return {
        label: `第 ${m[1]} 章 · 第 ${m[2]} 节 · 第 ${m[3]} 张`,
        chapterId: `ch${m[1]}`,
        sectionId: `s${m[2]}`,
      }
    }
    return { label: cardId, chapterId: 'ch1', sectionId: 's1' }
  }

  const formatTime = (ts: number) => {
    const now = Date.now()
    const diff = now - ts
    if (diff < 60_000) return '刚刚'
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`
    return `${Math.floor(diff / 86_400_000)} 天前`
  }

  // 排序/过滤错题列表
  const filteredEntries = mistakeEntries.filter((entry) => {
    if (filter === 'frequent') return entry.errorCount >= 2
    return true
  }).sort((a, b) => {
    if (filter === 'recent') return b.lastWrongAt - a.lastWrongAt
    return b.errorCount - a.errorCount
  })

  return (
    <div className="min-h-screen p-4 md:p-6 crt-scanlines">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/"
          className="font-pixel-display text-[10px] text-[color:var(--color-cyan)] hover:text-[color:var(--color-bull)] mb-3 inline-block"
        >
          ◀ 返回首页
        </Link>

        {/* 标题 */}
        <div className="pixel-box pixel-box-bright p-4 mb-4 text-center">
          <div className="font-pixel text-[10px] text-[color:var(--color-bear)] mb-2 tracking-wider">
            ► 错题本
          </div>
          <h1 className="font-pixel text-xl text-[color:var(--color-text-bright)] mb-1">
            答错的卡片
          </h1>
          <p className="font-pixel-body text-sm text-[color:var(--color-text-dim)] mt-1">
            ► 共 {mistakeEntries.length} 张卡 · 答对且掌握度≥60% 自动出册
          </p>

          {/* 筛选 Tab */}
          <div className="flex justify-center gap-2 mt-3 pt-3 border-t border-[color:var(--color-border)]">
            <button
              onClick={() => setFilter('all')}
              className={
                'pixel-button text-[10px] px-3 py-1 ' +
                (filter === 'all' ? 'pixel-button-primary' : '')
              }
            >
              全部 ({mistakeEntries.length})
            </button>
            <button
              onClick={() => setFilter('frequent')}
              className={
                'pixel-button text-[10px] px-3 py-1 ' +
                (filter === 'frequent' ? 'pixel-button-primary' : '')
              }
            >
              🔥 顽固错题 (≥2次)
            </button>
            <button
              onClick={() => setFilter('recent')}
              className={
                'pixel-button text-[10px] px-3 py-1 ' +
                (filter === 'recent' ? 'pixel-button-primary' : '')
              }
            >
              ⏱️ 最近答错
            </button>
          </div>
        </div>

        {/* 错题列表 */}
        <div className="space-y-2">
          {filteredEntries.map((entry: MistakeEntry) => {
            const { label, chapterId, sectionId } = parseCardPath(entry.cardId)
            return (
              <div key={entry.cardId} className="pixel-box p-3 hover:border-[color:var(--color-border-bright)] transition-colors">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="font-pixel text-[9px] text-[color:var(--color-gold)] mb-1 tracking-wider">
                      {label}
                    </div>
                    <div className="font-pixel-body text-sm text-[color:var(--color-text-bright)] mb-2">
                      错 <span className="text-[color:var(--color-bear)] font-bold">{entry.errorCount}</span> 次
                      {entry.wrongChoices.length > 0 && (
                        <span className="text-[color:var(--color-text-dim)]">
                          {' '}· 曾错选 {entry.wrongChoices.map(i => String.fromCharCode(65 + i)).join(' / ')}
                        </span>
                      )}
                    </div>
                    <div className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)]">
                      上次答错: {formatTime(entry.lastWrongAt)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <Link
                      to={`/study/${chapterId}/${sectionId}`}
                      className="pixel-button pixel-button-primary text-[10px] text-center"
                    >
                      ► 去特训
                    </Link>
                    <button
                      onClick={() => removeMistake(entry.cardId)}
                      className="pixel-button text-[10px]"
                      title="从错题本移除"
                    >
                      移除
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* 底部说明 */}
        <div className="mt-4 text-center font-pixel-display text-[10px] text-[color:var(--color-text-dim)]">
          ► 答对 + mastery ≥ 60 自动从错题本清除
        </div>
      </div>
    </div>
  )
}

export default MistakeBookPage